import { useCallback, useMemo, useState } from 'react';
import { sortDirections, sortFields } from '~/clientApi/agencies.model';
import { allowedSortFields } from '~/searchApi';

import { ColumnSocialPlatform } from '../components/ListViewRoster';

const descFirstFields: Array<allowedSortFields> = [
  ColumnSocialPlatform.instagram,
  ColumnSocialPlatform.tiktok,
  ColumnSocialPlatform.snapchat,
  ColumnSocialPlatform.youtube,
];
export const useFilterSortFields = ({
  field = 'name',
  direction = 'asc',
}: {
  field?: sortFields;
  direction?: sortDirections;
}) => {
  const [sortDirection, setSortDirection] = useState<sortDirections>(direction);
  const [sortField, setSortField] = useState<sortFields>(field);

  const toggleOrder = useCallback(
    (field: sortFields) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else if (descFirstFields.includes(field)) {
        setSortDirection('desc');
      } else {
        setSortDirection('asc');
      }
      setSortField(field);
    },
    [sortDirection, sortField],
  );

  const stringToSortFieldMap: Record<string, sortFields> = useMemo(() => {
    return {
      location: 'location',
      totalAudience: 'audience',
    };
  }, []);

  const giveSortFieldFromString = useCallback(
    (field: string) => {
      return stringToSortFieldMap[field] || field;
    },
    [stringToSortFieldMap],
  );

  const setSortingOrder = useCallback(
    (field: sortFields, direction: sortDirections) => {
      const sortablefield = giveSortFieldFromString(field);
      setSortField(sortablefield);
      setSortDirection(direction);
    },
    [giveSortFieldFromString, setSortDirection, setSortField],
  );

  const changeSortOrder = useCallback(
    (field: string, value: sortDirections) => {
      const sortablefield = giveSortFieldFromString(field);
      setSortField(sortablefield);
      setSortDirection(value);
    },
    [setSortField, setSortDirection, giveSortFieldFromString],
  );

  const isFieldSorted = useCallback(
    (field: string) => {
      return sortField === field ? sortDirection : false;
    },
    [sortField, sortDirection],
  );

  return {
    setSortingOrder,
    sortField: giveSortFieldFromString(sortField),
    sortDirection,
    toggleOrder,
    changeSortOrder,
    isFieldSorted,
  };
};
